import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { getCrew, UNASSIGNED_CREW_LABEL, isUnassignedCrew } from '../lib/crew'

const formatNotes = (text) => {
  if (!text) return null
  return text.replace(/fwd (?=\d)/g, 'fwd\n').split('\n').map((line, i) => (
    <div key={i}>{line}</div>
  ))
}

export default function PrintView() {
  const { date, crewId } = useParams()
  const [jobs, setJobs] = useState([])
  const [crewName, setCrewName] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let isMounted = true
    const load = async () => {
      if (!supabase) {
        if (isMounted) setLoading(false)
        return
      }
      try {
        let displayName = ''
        if (crewId === 'blank') {
          displayName = UNASSIGNED_CREW_LABEL
        } else {
          const allCrew = await getCrew()
          const crew = allCrew.find(c => c.id === crewId)
          displayName = crew?.name || crewId
        }
        if (isMounted) setCrewName(displayName)

        const { data, error } = await supabase
          .from('jobs')
          .select(`id, start_time, end_time, crew_name, comments, flag, property:properties(name, address, phone, maintenance_supervisor_name, maintenance_supervisor_phone, standing_directions, property_manager_name, property_manager_phone, unit_count)`)
          .eq('job_date', date)
          .neq('status', 'cancelled')
          .neq('is_deleted', true)
          .order('start_time')
        if (!error && isMounted) {
          const forCrew = (data || []).filter(j =>
            crewId === 'blank'
              ? isUnassignedCrew(j.crew_name)
              : j.crew_name === displayName
          )
          // Sort client-side so midnight jobs (stored as 00:00 by Postgres) appear last
          const sorted = [...forCrew].sort((a, b) => {
            const norm = t => (t === '00:00:00' || t === '00:00') ? '24:00:00' : t
            return norm(a.start_time).localeCompare(norm(b.start_time))
          })
          setJobs(sorted)
        }
      } catch (e) {
        console.warn('Could not load jobs for print:', e.message)
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    load()
    return () => { isMounted = false }
  }, [date, crewId])

  useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => window.print(), 500)
      return () => clearTimeout(timer)
    }
  }, [loading])

  const formattedDate = date
    ? new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      })
    : ''

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-500">Loading schedule...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white p-6 font-mono text-sm print-outer">
      <style>{`
        @media print {
          @page { margin: 0.5in; size: landscape; }
          .print-outer { padding: 0 !important; min-height: 0 !important; }
          .no-print { display: none !important; }
          .print-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          .print-table thead tr th { padding-bottom: 6px; }
        }
        .print-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
      `}</style>
      {/* Controls — hidden when printing */}
      <div className="no-print mb-6 flex gap-3">
        <button
          onClick={() => window.print()}
          className="px-4 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700"
        >
          🖨 Print / Save PDF
        </button>
        <button
          onClick={() => { window.location.href = `/?date=${date}` }}
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
        >
          Close
        </button>
      </div>

      <div className="max-w-6xl mx-auto">
        {jobs.length === 0 ? (
          <>
            {/* Header */}
            <div style={{ display: 'table', width: '100%', borderBottom: '2px solid black', paddingBottom: '4px', marginBottom: '12px' }}>
              <div style={{ display: 'table-cell', verticalAlign: 'bottom' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Schedule</div>
                <div style={{ fontSize: '1rem', fontWeight: '600' }}>{crewName}</div>
              </div>
              <div style={{ display: 'table-cell', verticalAlign: 'bottom', textAlign: 'right', fontSize: '0.875rem' }}>{formattedDate}</div>
            </div>
            <div className="text-center py-8 text-gray-500">
              No jobs scheduled for this crew on this date.
            </div>
          </>
        ) : (
          <table className="print-table">
            {/* thead repeats on every printed page */}
            <thead>
              <tr>
                <th colSpan="3" style={{ fontWeight: 'normal', textAlign: 'left', paddingBottom: '4px', borderBottom: '2px solid black' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td style={{ verticalAlign: 'bottom' }}>
                          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', lineHeight: '1.2' }}>Schedule</div>
                          <div style={{ fontSize: '1rem', fontWeight: '600' }}>{crewName}</div>
                        </td>
                        <td style={{ verticalAlign: 'bottom', textAlign: 'right', fontSize: '0.875rem' }}>{formattedDate}</td>
                      </tr>
                    </tbody>
                  </table>
                </th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job, idx) => {
                const prop = job.property || {}

                return (
                  <tr key={job.id} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                    {/* Col 1: Property info */}
                    <td style={{ width: '44%', verticalAlign: 'top', padding: '10px 8px 10px 0', borderBottom: '1px solid #9ca3af' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '0.95rem', lineHeight: '1.3' }}>{prop.name || '—'}</div>
                      <div style={{ fontSize: '0.85rem', marginTop: '2px' }}>{prop.address}</div>
                      {prop.standing_directions && (
                        <div style={{ fontSize: '0.75rem', marginTop: '3px' }}>
                          <span style={{ fontWeight: '600' }}>Directions: </span>{prop.standing_directions}
                        </div>
                      )}
                      {prop.property_manager_name && (
                        <div style={{ fontSize: '0.75rem', marginTop: '2px' }}>
                          Mgr: {prop.property_manager_name}{prop.property_manager_phone ? ` ${prop.property_manager_phone}` : ''}
                        </div>
                      )}
                      {prop.maintenance_supervisor_name && (
                        <div style={{ fontSize: '0.75rem', marginTop: '1px' }}>
                          Maint Super: {prop.maintenance_supervisor_name}{prop.maintenance_supervisor_phone ? ` ${prop.maintenance_supervisor_phone}` : ''}
                        </div>
                      )}
                      {!prop.maintenance_supervisor_name && prop.maintenance_supervisor_phone && (
                        <div style={{ fontSize: '0.75rem', marginTop: '1px' }}>
                          Maint: {prop.maintenance_supervisor_phone}
                        </div>
                      )}
                      {prop.unit_count && (
                        <div style={{ fontSize: '0.75rem', marginTop: '1px' }}>{prop.unit_count} units</div>
                      )}
                    </td>

                    {/* Col 2: AM notes */}
                    <td style={{ width: '40%', verticalAlign: 'top', padding: '10px 8px', borderBottom: '1px solid #9ca3af' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: '600', marginBottom: '2px' }}>AM</div>
                      <div style={{ fontSize: '0.75rem' }}>
                        {formatNotes(job.comments)}
                        {job.flag === 'urgent' && (
                          <div style={{ marginTop: '2px', fontWeight: 'bold' }}>⚑ URGENT</div>
                        )}
                      </div>
                    </td>

                    {/* Col 3: Phone + Total */}
                    <td style={{ width: '16%', verticalAlign: 'top', padding: '10px 0 10px 8px', borderBottom: '1px solid #9ca3af', textAlign: 'left' }}>
                      {prop.phone && (
                        <div style={{ fontSize: '0.75rem' }}>Phone: {prop.phone}</div>
                      )}
                      <div style={{ fontSize: '0.75rem', marginTop: '16px' }}>Total: ________</div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
